import * as Types from '@aeppic/types'
import { getDocumentId } from '../../model/index.js'

export const SERVICE_FORM_ID = '53ccd797-d0e6-49ca-9c76-7c5337af88ed'
export const SERVICE_DEFINITION_FORM_ID = 'c0d56b6e-a7f6-4eaf-9e3b-cfc592ade3be'
export const GLOBAL_SERVICES_FOLDER_ID = 'd06f9f16-07e0-48e8-89aa-8ccee83ce6c2'

export type ServiceSelector = string | {
  definition?: string | Types.IdOrReference | Types.Document
  definitionId?: string
  name?: string
  instance?: string | Types.IdOrReference | Types.Document
  instanceId?: string
}

export interface ServiceFindOptions {
  name?: string
  enabledOnly?: boolean
  includeDeveloperMode?: boolean
  includeInvalid?: boolean
  includeUnavailable?: boolean
}

type EffectiveServiceFindOptions = ServiceFindOptions & {
  enabledOnly: boolean
  includeDeveloperMode: boolean
  includeInvalid: boolean
  includeUnavailable: boolean
}

export interface ServiceSettingsDocuments {
  serviceSettings: Types.Document | null
}

export interface ServiceMatch {
  id: string
  name: string
  enabled: boolean
  devMode: boolean
  invalid: boolean
  available: boolean
  typeName: string
  instance: Types.Document
  type: Types.Document | null
  score: number
}

export default class Services {
  public Aeppic = null
  private _scope: Types.IdOrReference | Types.Document | null = null

  constructor(Aeppic, scope?: Types.IdOrReference | Types.Document | null) {
    this.Aeppic = Aeppic
    this._scope = scope ?? null
  }

  at(scope: Types.IdOrReference | Types.Document | null) {
    return new Services(this.Aeppic, scope)
  }

  service(selector: ServiceSelector, options: ServiceFindOptions = {}) {
    return new ServiceSelection(this, selector, options)
  }

  async findAll(selector: ServiceSelector, options: ServiceFindOptions = {}): Promise<ServiceMatch[]> {
    const effectiveOptions = {
      enabledOnly: true,
      includeDeveloperMode: false,
      includeInvalid: false,
      includeUnavailable: false,
      ...options,
    }

    const serviceInstances = await this.Aeppic.find(`f.id:${SERVICE_FORM_ID}`)
    const serviceTypeIds = Array.from(new Set(serviceInstances.map(instance => instance?.data?.instanceOf?.id).filter(Boolean)))
    const serviceTypes = await this.Aeppic.getAll(serviceTypeIds)
    const serviceTypesById = new Map<string, Types.Document>(serviceTypes.filter(Boolean).map(type => [type.id, type]))

    const scopeDocument = await this.getScopeDocument()
    const scopeChain = getScopeChain(scopeDocument)
    const normalizedSelector = normalizeSelector(selector)

    return serviceInstances
      .map(instance => this.buildServiceMatch(instance, serviceTypesById.get(getReferenceId(instance?.data?.instanceOf)) ?? null, scopeChain))
      .filter(match => isEligibleForScope(match.instance, scopeChain))
      .filter(match => this.matches(match, normalizedSelector, effectiveOptions))
      .sort(compareServiceMatches)
  }

  async getScopeDocument(): Promise<Types.Document | null> {
    const explicitScopeId = getDocumentId(this._scope)
    if (explicitScopeId) {
      return this.Aeppic.get(explicitScopeId)
    }

    const contextRootId = (this.Aeppic as any).context?.rootDocumentId
    if (contextRootId) {
      const contextRoot = await this.Aeppic.get(contextRootId)
      if (contextRoot) {
        return contextRoot
      }
    }

    const account = this.Aeppic.Account
    const accountRootId = account?.a?.[0] ?? account?.id
    if (accountRootId) {
      const accountRoot = await this.Aeppic.get(accountRootId)
      if (accountRoot) {
        return accountRoot
      }
    }

    return null
  }

  async getServiceSettingsDocument(service: ServiceMatch): Promise<Types.Document | null> {
    const settingsId = getReferenceId(service?.instance?.data?.settings)
    if (!settingsId) {
      return null
    }

    return this.Aeppic.get(settingsId)
  }

  async getSettingsDocuments(service: ServiceMatch): Promise<ServiceSettingsDocuments> {
    const serviceSettings = await this.getServiceSettingsDocument(service)

    return {
      serviceSettings,
    }
  }

  private buildServiceMatch(instance: Types.Document, type: Types.Document | null, scopeChain: string[]): ServiceMatch {
    const invalid = !type
    const enabled = instance?.data?.enabled === true
    const devMode = instance?.data?.devMode === true
    const available = type?.data?.available === true
    const instanceName = String(instance?.data?.name || instance?.id || '')
    const typeName = String(type?.data?.name || getReferenceText(instance?.data?.instanceOf) || type?.id || '')

    return {
      id: instance.id,
      name: instanceName,
      enabled,
      devMode,
      invalid,
      available,
      typeName,
      instance,
      type,
      score: scoreByScope(instance, scopeChain),
    }
  }

  private matches(match: ServiceMatch, selector: NormalizedServiceSelector, options: EffectiveServiceFindOptions) {
    if (options.enabledOnly && !match.enabled) {
      return false
    }

    if (!options.includeDeveloperMode && match.devMode) {
      return false
    }

    if (!options.includeInvalid && match.invalid) {
      return false
    }

    if (!options.includeUnavailable && !match.available) {
      return false
    }

    if (options.name && !matchesKey(options.name, [match.name, match.id])) {
      return false
    }

    if (selector.instance && !matchesKey(selector.instance, [match.id, match.name])) {
      return false
    }

    if (selector.name && !matchesKey(selector.name, [match.name])) {
      return false
    }

    if (!selector.definition) {
      return true
    }

    return matchesKey(selector.definition, [
      match.typeName,
      match.type?.id,
      getReferenceId(match.instance?.data?.instanceOf),
      getReferenceText(match.instance?.data?.instanceOf),
    ])
  }
}

export class ServiceSelection {
  private _services: Services
  private _selector: ServiceSelector
  private _options: ServiceFindOptions

  constructor(services: Services, selector: ServiceSelector, options: ServiceFindOptions = {}) {
    this._services = services
    this._selector = selector
    this._options = options
  }

  at(scope: Types.IdOrReference | Types.Document | null) {
    return this._services.at(scope).service(this._selector, this._options)
  }

  async all(): Promise<ServiceMatch[]> {
    return this._services.findAll(this._selector, this._options)
  }

  async get(): Promise<ServiceMatch | null> {
    const [service] = await this.all()
    return service ?? null
  }

  async required(): Promise<ServiceMatch> {
    const service = await this.get()
    if (!service) {
      throw new Error(`No enabled service found for ${JSON.stringify(this._selector)}`)
    }

    return service
  }

  async settingsDocuments(): Promise<ServiceSettingsDocuments> {
    const service = await this.required()
    return this._services.getSettingsDocuments(service)
  }

  async settings() {
    const documents = await this.settingsDocuments()
    return documents.serviceSettings?.data ?? null
  }

  async setting(key: string) {
    const settings = await this.settings()
    return settings?.[key] ?? null
  }
}

function getScopeChain(scopeDocument: Types.Document | null): string[] {
  if (!scopeDocument) {
    return []
  }

  return [...(scopeDocument.a ?? []), scopeDocument.id].filter(Boolean)
}

function scoreByScope(instance: Types.Document, scopeChain: string[]) {
  if (!scopeChain.length) {
    return 0
  }

  const serviceChain = [...(instance.a ?? []), instance.p, instance.id].filter(Boolean)

  let score = 0
  for (const serviceScope of serviceChain) {
    const scopeIndex = scopeChain.indexOf(serviceScope)
    if (scopeIndex >= 0) {
      score = Math.max(score, scopeIndex + 1)
    }
  }

  return score
}

function isEligibleForScope(instance: Types.Document, scopeChain: string[]) {
  if (!scopeChain.length) {
    return true
  }

  const serviceChain = getDocumentChain(instance)

  return serviceChain.some(id => scopeChain.includes(id)) || serviceChain.includes(GLOBAL_SERVICES_FOLDER_ID)
}

function getDocumentChain(document: Types.Document) {
  return [...(document.a ?? []), document.p, document.id].filter(Boolean)
}

function compareServiceMatches(a: ServiceMatch, b: ServiceMatch) {
  if (a.score !== b.score) {
    return b.score - a.score
  }

  return a.id.localeCompare(b.id)
}

type NormalizedServiceSelector = {
  definition?: string
  name?: string
  instance?: string
}

function normalizeSelector(selector: ServiceSelector): NormalizedServiceSelector {
  if (typeof selector === 'string') {
    return {
      definition: selector,
    }
  }

  const definition = selector.definition ?? selector.definitionId
  const instance = selector.instance ?? selector.instanceId

  return {
    definition: definition ? String(getDocumentId(definition) || getReferenceText(definition) || definition) : null,
    name: selector.name ?? null,
    instance: instance ? String(getDocumentId(instance) || getReferenceText(instance) || instance) : null,
  }
}

function matchesKey(key: string, candidates: any[]) {
  const normalizedKey = normalizeServiceKey(key)
  if (!normalizedKey) {
    return false
  }

  return candidates
    .filter(Boolean)
    .map(candidate => normalizeServiceKey(String(candidate)))
    .filter(Boolean)
    .some(candidate => candidate === normalizedKey || candidate.includes(normalizedKey) || normalizedKey.includes(candidate))
}

function normalizeServiceKey(value: string) {
  return value
    .toLowerCase()
    .replace(/\(template\)/g, '')
    .replace(/\b(service|instance)\b/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function getReferenceId(value: any) {
  return getDocumentId(value) || null
}

function getReferenceText(value: any) {
  if (!value || typeof value !== 'object') {
    return null
  }

  return value.text || null
}
